import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import classnames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { useDropzone } from 'react-dropzone';
import { Loader, IconButton } from '@grandvisionhq/common';
import { Text } from '@grandvisionhq/elements';
import { Icon } from '@grandvisionhq/icons';
import { useIntl } from '@grandvisionhq/state';
import { isUploadError, isUploadSuccess, isUploadLoading } from '../types.js';

const iconSharedProps = {
    initial: {
        opacity: 0,
        scale: 0
    },
    animate: {
        opacity: 1,
        scale: 1
    },
    exit: {
        opacity: 0,
        scale: 0
    }
};
const Upload = ({ variant ='vertical' , id , 'data-t': dataT , iconName , disabled =false , maximumFileSizeBytes , allowedFileTypes , title , uploadStatus , onFileAccepted , onFileRejected , onDelete , onCancel , className , 'aria-describedby': ariaDescribedBy  })=>{
    const { getFormattedLabel , getLabel , hasLabel  } = useIntl();
    const descriptionId = `description-${id}`;
    const inputId = `file-input-${id}`;
    let ariaDescribedByIDList = [
        descriptionId,
        ariaDescribedBy
    ].filter(Boolean);
    if (isUploadError(uploadStatus) && uploadStatus.errorMessageId) {
        ariaDescribedByIDList = [
            uploadStatus.errorMessageId,
            ...ariaDescribedByIDList
        ];
    }
    // This should be text like "File upload" to describe what kind of component this is.
    const uploadScreenReaderRole = hasLabel('upload.screenreader.title') ? getLabel('upload.screenreader.title') : undefined;
    const uploadedFileName = isUploadSuccess(uploadStatus) ? uploadStatus.uploadReference.fileName : undefined;
    const uploadScreenReaderName = !!uploadedFileName ? `${title} ${uploadedFileName}` : title;
    const onFileDropSuccess = React.useCallback((files)=>{
        const file = files[0] // because uploading multiple files is disabled, we only need to take the first one.
        ;
        if (file) onFileAccepted(file);
    }, [
        onFileAccepted
    ]);
    const onFileDropRejected = React.useCallback((fileRejections)=>{
        const fileRejection = fileRejections[0];
        if (fileRejection) onFileRejected(fileRejection);
    }, [
        onFileRejected
    ]);
    const onDeleteClick = React.useCallback((event)=>{
        var ref;
        event.stopPropagation();
        onDelete();
        (ref = rootRef.current) === null || ref === void 0 ? void 0 : ref.focus();
    }, [
        onDelete
    ]);
    const onCancelClick = React.useCallback((event)=>{
        var ref;
        event.stopPropagation();
        onCancel();
        (ref = rootRef.current) === null || ref === void 0 ? void 0 : ref.focus();
    }, [
        onCancel
    ]);
    const isDisabled = disabled || isUploadLoading(uploadStatus);
    const { getRootProps , getInputProps , isDragActive , isDragAccept , isDragReject , rootRef  } = useDropzone({
        accept: allowedFileTypes,
        maxFiles: 1,
        multiple: false,
        maxSize: maximumFileSizeBytes,
        onDropAccepted: onFileDropSuccess,
        onDropRejected: onFileDropRejected,
        disabled: isDisabled
    });
    const iconSize = variant === 'vertical' ? 84 : 48;
    return /*#__PURE__*/ React.createElement("div", swcHelpers.extends({}, getRootProps(), {
        tabIndex: isDisabled ? -1 : 0,
        className: classnames('upload', `upload--${variant}`, className, {
            'upload--file-hover': isDragActive,
            'upload--active': isDragActive || isUploadSuccess(uploadStatus),
            'upload--accept': isDragAccept || isUploadSuccess(uploadStatus),
            'upload--reject': isDragReject,
            'upload--error': isUploadError(uploadStatus),
            'upload--loading': isUploadLoading(uploadStatus)
        }),
        id: id,
        "data-t": dataT,
        role: "button",
        "aria-label": uploadScreenReaderName,
        "aria-describedby": ariaDescribedByIDList.join(' '),
        "aria-roledescription": uploadScreenReaderRole
    }), /*#__PURE__*/ React.createElement("div", {
        className: "upload__inner"
    }, /*#__PURE__*/ React.createElement(AnimatePresence, null, isUploadSuccess(uploadStatus) && /*#__PURE__*/ React.createElement(motion.div, swcHelpers.extends({
        className: "upload__icon upload__icon--valid"
    }, iconSharedProps), /*#__PURE__*/ React.createElement(Icon, {
        name: "checkmark"
    })), isUploadError(uploadStatus) && /*#__PURE__*/ React.createElement(motion.div, swcHelpers.extends({
        className: "upload__icon upload__icon--invalid"
    }, iconSharedProps), /*#__PURE__*/ React.createElement(Icon, {
        name: "alert-circle"
    })), isUploadLoading(uploadStatus) && /*#__PURE__*/ React.createElement(motion.div, swcHelpers.extends({
        className: "upload__icon upload__icon--loading"
    }, iconSharedProps), /*#__PURE__*/ React.createElement("div", {
        className: "sr-only",
        role: "alert"
    }, getLabel('common.loadingText')), /*#__PURE__*/ React.createElement(Loader, {
        className: "upload__loader"
    }))), /*#__PURE__*/ React.createElement(Icon, {
        width: iconSize,
        height: iconSize,
        name: iconName,
        className: "upload__type-icon"
    }), /*#__PURE__*/ React.createElement("label", {
        className: "upload__label",
        htmlFor: inputId
    }, isUploadSuccess(uploadStatus) ? /*#__PURE__*/ React.createElement(Text, {
        variant: "body",
        className: "upload__file-name"
    }, uploadStatus.uploadReference.fileName) : /*#__PURE__*/ React.createElement("span", {
        className: "upload__title"
    }, title)), /*#__PURE__*/ React.createElement("input", swcHelpers.extends({}, getInputProps({
        disabled
    }), {
        "aria-describedby": ariaDescribedByIDList.join(' '),
        "data-t": "upload-input",
        id: inputId
    })), isUploadSuccess(uploadStatus) && /*#__PURE__*/ React.createElement(IconButton, {
        onClick: onDeleteClick,
        "aria-label": `${getLabel('common.actions.remove')} ${uploadedFileName}`,
        className: "upload__delete"
    }, /*#__PURE__*/ React.createElement(Icon, {
        name: "trash"
    })), isUploadLoading(uploadStatus) && /*#__PURE__*/ React.createElement(IconButton, {
        onClick: onCancelClick,
        "aria-label": getLabel('common.actions.cancel'),
        className: "upload__delete"
    }, /*#__PURE__*/ React.createElement(Icon, {
        name: "trash"
    }))), /*#__PURE__*/ React.createElement("div", {
        id: descriptionId,
        className: "upload__description"
    }, /*#__PURE__*/ React.createElement(Text, {
        variant: "body-head"
    }, getFormattedLabel('upload.description', {
        parseHTML: true
    }))));
};

export { Upload };
//# sourceMappingURL=Upload.js.map
